<template>
  <div class="navbar-container d-flex content align-items-center">
    <!-- Nav Menu Toggler -->
    <ul class="nav navbar-nav d-xl-none">
      <li class="nav-item">
        <b-link
          class="nav-link"
          @click="toggleVerticalMenuActive"
        >
          <feather-icon
            icon="MenuIcon"
            size="21"
          />
        </b-link>
      </li>
    </ul>

    <!-- Left Col -->
    <div
      class="bookmark-wrapper align-items-center flex-grow-1 d-none d-lg-flex"
    >
      <!-- <dark-Toggler class="d-none d-lg-block" /> -->
    </div>

    <b-navbar-nav class="nav align-items-center ml-auto">
      <locale />
      <notification-dropdown />
    </b-navbar-nav>

    <b-navbar-nav class="nav align-items-center ml-auto">
      <b-nav-item-dropdown
        right
        toggle-class="d-flex align-items-center dropdown-user-link"
        class="dropdown-user"
      >
        <template #button-content>
          <div @click="handleClickNav">
            <div class="d-sm-flex d-none user-nav">
              <p class="user-name font-weight-bolder mb-0 text-capitalize">
                {{ username }}
              </p>
              <span class="user-status">{{ companyName }}</span>
            </div>
            <b-avatar
              size="40"
              variant="light-primary"
              badge
              :src="profilePicture"
              :text="avatarText(name)"
              class="badge-minimal"
              :badge-variant="`${
                $store.state.profile.isOffline ? 'dark' : 'success'
              }`"
            >
              <feather-icon
                v-if="!name"
                icon="UserIcon"
                size="22"
              />
            </b-avatar>
          </div>
        </template>

        <b-dropdown-item
          link-class="d-flex align-items-center"
          :to="{ name: 'account-setting' }"
        >
          <feather-icon
            size="16"
            icon="SettingsIcon"
            class="mr-50"
          />
          <span>{{ $t('breadcrumb.accountSettings') }}</span>
        </b-dropdown-item>

        <b-dropdown-divider />

        <b-dropdown-item class="custom-nav-item p-0">
          <b-form-checkbox
            v-model="isOnline"
            class="pt-1.5"
            name="summary-toggle"
            switch
            :disabled="loading"
            @input="changeOnline"
            @click.native.stop=""
          >
            <p class="font-weight-bold">
              {{ $t(`general.online`) }}
            </p>
          </b-form-checkbox>
        </b-dropdown-item>

        <b-dropdown-divider />

        <b-dropdown-item
          v-if="isBreakTimeConfigured"
          class="custom-nav-item p-0 break-time"
        >
          <div class="break-time-container">
            <div class="break-time-stats mb-1">
              <div class="stat-item">
                <span class="stat-label">{{ $t('general.break_time_duration') }}</span>
                <span class="stat-value">{{
                  $tc(
                    'general.break_time_duration_minute',
                    breakTimeData.breakDuration || 1,
                    { minute: breakTimeData.breakDuration }
                  )
                }}</span>
              </div>
              <div
                v-if="breakTimeData.expiredAt && formatCountdown"
                class="stat-item break-countdown"
              >
                <span class="stat-label">{{ $t('general.timeRemaining') }}</span>
                <span
                  class="stat-value"
                  :class="{ 'text-danger': remainingTime <= 300000 }"
                >
                  {{ formatCountdown }}
                </span>
              </div>
            </div>
            <b-button
              variant="primary"
              size="sm"
              class="btn-break"
              :disabled="breakTimeData.availableBreakTime === 0 && !breakTimeData.expiredAt"
              @click="startStopBreakTime"
            >
              <feather-icon
                style="flex-shrink: 0;"
                :icon="breakTimeData.expiredAt ? 'PlayIcon' : 'CoffeeIcon'"
                size="14"
              />
              {{
                breakTimeData.expiredAt
                  ? $t('general.resume_work')
                  : $t('general.take_break') + ` (${breakTimeData.availableBreakTime}/${breakTimeData.totalBreakTime})`
              }}
            </b-button>
          </div>
        </b-dropdown-item>

        <b-dropdown-divider v-if="isBreakTimeConfigured" />

        <b-dropdown-item
          link-class="d-flex align-items-center"
          @click="logout"
        >
          <feather-icon
            size="16"
            icon="LogOutIcon"
            class="mr-50"
          />
          <span>{{ $t('general.logout') }}</span>
        </b-dropdown-item>
      </b-nav-item-dropdown>
    </b-navbar-nav>
  </div>
</template>

<script>
import {
  BLink,
  BNavbarNav,
  BNavItemDropdown,
  BDropdownItem,
  BDropdownDivider,
  BAvatar,
  BFormCheckbox,
  BButton,
} from 'bootstrap-vue'
import { logout } from '@/auth/utils'
import { avatarText } from '@core/utils/filter'
import Locale from '@/layouts/components/app-navbar/Locale.vue'
import NotificationDropdown from '@/layouts/components/app-navbar/NotificationDropdown.vue'
import Repository from '@/repositories/RepositoryFactory'
import { connectSocket } from '@/libs/socket'
import useJwt from '@/auth/jwt/useJwt'
import useCallStore from '@/store-pinia/useCallStore'
import useTicketStore from '@/store-pinia/useTicketStore'
import ToastificationAvatar from '@core/components/toastification/ToastificationAvatar.vue'

const breakTimeRepository = Repository.get('breakTime')
const ProfileRepository = Repository.get('profile')
const AuthRepository = Repository.get('auth')
const localUserData = JSON.parse(
  localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
)

export default {
  components: {
    BLink,
    BNavbarNav,
    BNavItemDropdown,
    BDropdownItem,
    BDropdownDivider,
    BAvatar,
    BFormCheckbox,
    BButton,

    // Navbar Components
    Locale,
    NotificationDropdown,
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      avatarText,
      isOnline: !localUserData?.isOffline,
      socket: null,
      loading: false,
      breakTimeData: {
        totalBreakTime: 0,
        breakTime: 0,
        availableBreakTime: 0,
        expiredAt: null,
      },
      isBreakTimeLoading: false,
      countdownInterval: null,
      remainingTime: 0,
      updateTimeoutId: null,
      profilePicture: '',
    }
  },
  computed: {
    isBreakTimeConfigured() {
      return this.breakTimeData.totalBreakTime > 0
    },
    userData() {
      return this.$store.state.profile.email
          || this.$store.state.profile.phone
        ? this.$store.state.profile
        : localUserData
    },
    name() {
      if (this.$store.state.profile?.firstName) {
        return this.$store.state.profile.firstName
      }

      return this.userData?.firstName
    },
    companyName() {
      if (this.$store.state.profile?.companyName) {
        return this.$store.state.profile.companyName
      }

      return this.userData?.companyName
    },
    username() {
      if (this.$store.state.profile.firstName) {
        return this.$store.state.profile.firstName
      }

      return this.userData.firstName
    },
    formatCountdown() {
      if (!this.remainingTime) return ''

      const minutes = Math.floor(this.remainingTime / 60000)
      const seconds = Math.floor((this.remainingTime % 60000) / 1000)

      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    },
  },
  watch: {
    '$store.state.profile.profilePicture': {
      immediate: true,
      handler(newVal) {
        this.profilePicture = newVal
      },
    },
    'breakTimeData.expiredAt': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.startCountdown()
        } else {
          this.stopCountdown()
        }
      },
    },
    remainingTime(newVal) {
      if (newVal <= 0 && this.breakTimeData.expiredAt) {
        this.handleBreakTimeExpired()
        this.breakTimeData.expiredAt = null
        this.breakTimeData.breakTime = Math.max(this.breakTimeData.breakTime - 1, 0)
      }
    },
  },
  mounted() {
    this.connectSocket()
    this.fetchBreakTimeData()
  },
  beforeDestroy() {
    this.stopCountdown()

    // Clear any pending timeouts
    if (this.updateTimeoutId) {
      clearTimeout(this.updateTimeoutId)
      this.updateTimeoutId = null
    }
  },
  methods: {
    updateBreakTime() {
      breakTimeRepository
        .available()
        .then(res => {
          this.breakTimeData = res.data.data || {}
        })
        .catch(() => {
          // console.error('Error fetching available break time:', error)
        })
    },
    handleClickNav() {
      this.updateBreakTime()
    },
    startStopBreakTime() {
      if (this.breakTimeData.expiredAt) {
        this.stopBreakTime()
      } else {
        this.startBreakTime()
      }
    },
    stopBreakTime() {
      this.$bvModal
        .msgBoxConfirm(this.$t('alert.sureToResumeWork'), {
          title: this.$t('alert.areYouSure'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: this.$t('button.confirm'),
          cancelTitle: this.$t('button.cancel'),
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            // Stop the countdown timer first
            this.stopCountdown()

            breakTimeRepository.resume().then(() => {
              this.$toast({
                component: ToastificationAvatar,
                position: 'top-right',
                timeout: 10000,
                props: {
                  title: this.$t('general.resume_work'),
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: this.$t('alert.resume_work_success'),
                },
              })

              // Set expiredAt to null to indicate break time is over
              this.breakTimeData.expiredAt = null
              // Update break time data after resume
              this.updateBreakTime()
            })
          }
        })
    },
    startBreakTime() {
      this.$bvModal
        .msgBoxConfirm(this.$t('alert.sureToTakeBreak'), {
          title: this.$t('alert.areYouSure'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'primary',
          okTitle: this.$t('button.confirm'),
          cancelTitle: this.$t('button.cancel'),
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            breakTimeRepository.enable().then(() => {
              this.$toast({
                component: ToastificationAvatar,
                position: 'top-right',
                timeout: 10000,
                props: {
                  title: this.$t('breadcrumb.breakTime'),
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: this.$t('alert.break_start_success'),
                },
              })
            })
            this.updateBreakTime()
          }
        })
    },
    async logout() {
      this.callStore.disposeAgent()
      await AuthRepository.logout()
      this.ticketStore.$reset()
      this.$store.state.profile.profilePicture = null
      logout()
      // this.$OneSignal.removeExternalUserId();
      this.$OneSignal.deleteTags(['merchantUserId', 'merchantId'])

      // Redirect to login page
      this.$router.push({ name: 'login' })
    },
    async submit() {
      const payload = {
        isOffline: !this.isOnline,
        socketId: this.socket.id,
      }

      this.loading = true
      ProfileRepository.updateOffline(payload)
        .then(response => {
          const user = response.data.data
          localStorage.setItem(
            useJwt.jwtConfig.storageUserDataKeyName,
            JSON.stringify(user),
          )
          this.$store.commit('profile/UPDATE_PROFILE', user)
          this.isEmailChanged = user.isEmailChanged
        })
        .catch(() => {})
        .then(() => {
          this.loading = false
        })
    },

    changeOnline() {
      this.$nextTick(() => {
        if (this.$store.state.profile.isOffline === this.isOnline) {
          this.submit()
        }
      })
    },
    connectSocket() {
      this.socket = connectSocket()
    },
    async fetchBreakTimeData() {
      try {
        const response = await breakTimeRepository.available()
        this.breakTimeData = response.data.data || {}
        if (this.breakTimeData.expiredAt) {
          this.startCountdown()
        }
      } catch (error) {
        console.error('Error fetching break time data:', error)
      }
    },
    startCountdown() {
      this.stopCountdown() // Clear any existing interval

      const updateCountdown = () => {
        const now = new Date().getTime()
        const expiredAt = new Date(this.breakTimeData.expiredAt).getTime()
        this.remainingTime = Math.max(0, expiredAt - now)

        if (this.remainingTime <= 0) {
          this.stopCountdown()
        }
      }

      updateCountdown() // Initial update
      this.countdownInterval = setInterval(updateCountdown, 1000)
    },
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    },
    handleBreakTimeExpired() {
      this.$toast({
        component: ToastificationAvatar,
        position: 'top-right',
        props: {
          title: this.$t('breadcrumb.breakTime'),
          icon: 'BellIcon',
          variant: 'warning',
          text: this.$t('alert.break_time_expired'),
        },
      })

      // Clear any existing update timeout
      if (this.updateTimeoutId) {
        clearTimeout(this.updateTimeoutId)
      }

      // Store the timeout ID so we can clear it if needed
      this.updateTimeoutId = setTimeout(() => {
        this.updateBreakTime()
        this.updateTimeoutId = null
      }, 5000)
    },
  },
  setup() {
    const callStore = useCallStore()
    const ticketStore = useTicketStore()

    return { callStore, ticketStore }
  },
}
</script>

<style lang="scss">
  .nav.navbar-nav {
    li.custom-nav-item {
      &:active,
      & a:active,
      &:hover,
      & a:hover {
        background: none;
      }
      .custom-control {
        justify-content: space-between;
        .custom-control-label {
          margin-bottom: 0;
        }
        p {
          margin-bottom: 0;
        }
      }
    }
  }

  .break-time {
    a {
      cursor: auto !important;
    }
  }

  .break-time-container {
    // background: #f8f9fa;
    border-radius: 8px;

    .break-time-stats {
      display: grid;
      margin-bottom: 1rem;

      .stat-item {
        text-align: start;

        .stat-label {
          text-align: start;
          display: block;
          font-size: 0.75rem;
          color: #6e6b7b;
          margin-bottom: 2px;
        }

        .stat-value {
          font-weight: 600;
          color: #5e5873;

          &.text-danger {
            color: #ea5455;
          }
        }

        &.break-countdown {
          margin-top: 4px;
          padding-top: 4px;
          border-top: 1px solid rgba(0, 0, 0, 0.05);
        }
      }
    }
  }

  .btn-break {
    width: 100%;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    background: #5e5873;
    color: #fff;
    transition: background 0.3s;

    &:hover {
      background: #4d4660;
    }

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }
  }
</style>
